import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { channelApi } from './channelApi';
import type {
  Channel,
  ChannelCreateRequest,
  ChannelStats,
  ChannelUpdateRequest,
} from '../model/types';

/**
 * Ключи запросов для каналов.
 *
 * После create/delete/update инвалидируем list данного ассистента.
 * Stats — отдельная ветка, инвалидируем её при update.
 *
 * detail-ключа НЕТ намеренно — один канал не запрашивается отдельным
 * GET'ом. Вместо этого useChannel(channelId) находит его в кэше list.
 * Подробнее см. комментарий к useChannel.
 */
export const channelQueryKeys = {
  all: ['channels'] as const,
  list: (assistantId: string) => [...channelQueryKeys.all, 'list', assistantId] as const,
  stats: (channelId: string) => [...channelQueryKeys.all, 'stats', channelId] as const,
};

/** GET /channels?assistant_id=... — список каналов ассистента. */
export function useChannels(assistantId: string | undefined) {
  return useQuery<Channel[], Error>({
    queryKey: channelQueryKeys.list(assistantId ?? ''),
    queryFn: () => channelApi.list(assistantId!),
    enabled: !!assistantId,
    staleTime: 10_000,
  });
}

/**
 * Получить один канал по ID.
 *
 * ВАЖНО — реализовано через `useChannels(assistantId).data.find(...)`, НЕ
 * через отдельный GET /channels/{id}. На бэке такого эндпоинта нет: роутер
 * channels.py поддерживает только GET /channels (список), GET /channels/{id}/stats,
 * PATCH /channels/{id}, DELETE /channels/{id}.
 *
 * Это нормально архитектурно: один канал — лёгкий объект, он всегда есть
 * в списке. После create/update/delete мутации инвалидируют queryKey list,
 * соответственно useChannel автоматически получит свежие данные.
 *
 * assistantId берётся из URL-параметра :id (react-router) — он есть на
 * странице /assistants/:id/channels/:channelId. Если страница откроется
 * вне этого URL — assistantId будет undefined, useChannels запрос пропустит,
 * вернёт isLoading=false и data=undefined → компонент покажет «не найден».
 *
 * Если юзер перешёл на эту страницу прямо по URL (refresh, поделились
 * ссылкой) — useChannels отработает с холодного кэша, через ~100мс
 * страница доедет.
 */
export function useChannel(channelId: string | undefined): {
  data: Channel | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const { id: assistantId } = useParams<{ id: string }>();
  const listQuery = useChannels(assistantId);

  if (!channelId) {
    return { data: undefined, isLoading: false, error: null };
  }
  if (listQuery.isLoading) {
    return { data: undefined, isLoading: true, error: null };
  }
  if (listQuery.error) {
    return { data: undefined, isLoading: false, error: listQuery.error };
  }
  const channel = listQuery.data?.find((c) => c.id === channelId);
  if (!channel) {
    return {
      data: undefined,
      isLoading: false,
      error: new Error('Канал не найден'),
    };
  }
  return { data: channel, isLoading: false, error: null };
}

/**
 * GET /channels/{id}/stats — агрегаты по каналу.
 */
export function useChannelStats(channelId: string | undefined, enabled = true) {
  return useQuery<ChannelStats, Error>({
    queryKey: channelQueryKeys.stats(channelId ?? ''),
    queryFn: () => channelApi.getStats(channelId!),
    enabled: !!channelId && enabled,
    staleTime: 30_000,
  });
}

/**
 * POST /channels — подключение нового канала.
 */
export function useCreateChannelMutation(assistantId: string) {
  const qc = useQueryClient();
  return useMutation<Channel, Error, ChannelCreateRequest>({
    mutationFn: (payload) => channelApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: channelQueryKeys.list(assistantId) });
    },
  });
}

/** PATCH /channels/{id} — переключение active ↔ paused. */
export function useUpdateChannelMutation(assistantId: string) {
  const qc = useQueryClient();
  return useMutation<Channel, Error, { channelId: string; payload: ChannelUpdateRequest }>({
    mutationFn: ({ channelId, payload }) => channelApi.update(channelId, payload),
    onSuccess: (_data, { channelId }) => {
      qc.invalidateQueries({ queryKey: channelQueryKeys.list(assistantId) });
      qc.invalidateQueries({ queryKey: channelQueryKeys.stats(channelId) });
    },
  });
}

/** DELETE /channels/{id} — отключение канала. */
export function useDeleteChannelMutation(assistantId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (channelId) => channelApi.delete(channelId),
    onSuccess: (_void, channelId) => {
      qc.removeQueries({ queryKey: channelQueryKeys.stats(channelId) });
      qc.invalidateQueries({ queryKey: channelQueryKeys.list(assistantId) });
    },
  });
}
